import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Events } from "ionic-angular";
import { PatientChartResponse } from "models/patient-chart/patientChartResponse";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { MobileUtilsProvider } from "providers/mobile-utils/mobile-utils";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { StateManagementService } from "../providers/state-management/state-management.service";

@Injectable()
export class NavigationService {
  public navObservable: Observable<any>;
  navObserver;

  navCheckObservable: Observable<any>;
  navCheckObserver;

  private newPatientProfileBaseUrl: string;
  private newPatientChartUrl: string;
  private newPatientChartData: any;
  private patientProfileUrl: string;

  constructor(
    public state: StateManagementService,
    private injector: Injector,
    private configurationService: ConfigurationService,
    private http: HttpClient,
    public events: Events,
    public stateData: StateManagementService,
    private mobileUtils: MobileUtilsProvider
  ) {
    this.navObservable = new Observable<any>((observer) => {
      this.navObserver = observer;
    });
  }
  initialize(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.navObservable) {
        resolve();
      } else {
        this.navObservable = new Observable<any>((observer) => {
          this.navObserver = observer;
        });
        resolve();
      }
    }).catch((error) => {
      console.error("Error initializing NavigationService:", error);
    });
  }
  getNavigationObservable(): Observable<any> {
    return this.navObservable;
  }

  notifyNavigationEvent(eventData: any) {
    if (this.navObserver) {
      this.navObserver.next(eventData);
    }
  }

  private patChartId = new BehaviorSubject<string>("undefined");
  currentPatChartId = this.patChartId.asObservable();

  changeID(message: any) {
    this.patChartId.next(message);
  }

  private qbosId = new BehaviorSubject<string>("undefined");
  currentQbosId = this.qbosId.asObservable();
  changeQbosID(message: any) {
    this.qbosId.next(message);
  }

  // In-flight navigation guard. NOVA-1652 / SUP-19613: Ionic 3's transition
  // pipeline can interleave two setRoot(...) calls fired back-to-back, mounting
  // two instances of the same page (new ngOnInit runs before old ngOnDestroy).
  // Duplicate emits for the same target page within the guard window are dropped.
  private pendingNavigationPage: string | null = null;
  private pendingNavigationTimer: any = null;
  private static readonly NAVIGATION_GUARD_MS = 1000;

  // Handle internal navigation by page / nav params
  goToInternalPage(page: string, navParams: object) {
    if (this.pendingNavigationPage === page) {
      return;
    }
    if (this.state === undefined) {
      this.state = this.injector.get(StateManagementService);
    }
    if (page !== "sign-in-page") {
      this.state.setCurrentPage(page, navParams);
    }
    this.markNavigationPending(page);
    this.attemptToNavigate(
      () => {
        this.navCheckObserver = null;
        // console.log( "Removed the nav check ovserver." );
        this.navObserver.next({ page: page, params: navParams });
      },
      () => {
        this.clearPendingNavigation();
        console.log("Navigation rejected!");
      }
    );
  }

  private markNavigationPending(page: string) {
    this.pendingNavigationPage = page;
    if (this.pendingNavigationTimer) {
      clearTimeout(this.pendingNavigationTimer);
    }
    this.pendingNavigationTimer = setTimeout(() => {
      this.pendingNavigationPage = null;
      this.pendingNavigationTimer = null;
    }, NavigationService.NAVIGATION_GUARD_MS);
  }

  private clearPendingNavigation() {
    this.pendingNavigationPage = null;
    if (this.pendingNavigationTimer) {
      clearTimeout(this.pendingNavigationTimer);
      this.pendingNavigationTimer = null;
    }
  }

  // Handle external navigation by url
  goToExternalPage(url: string) {
    // console.log( "Navigating to external page." );
    this.attemptToNavigate(
      () => {
        window.location.replace(url);
      },
      () => {
        // console.log( "Navigation rejected!" );
      }
    );
  }

  // Allow for other entities to subscribe and accept/reject navigation changes
  getNavCheckObservable() {
    if (!this.navCheckObservable) {
      this.navCheckObservable = new Observable<any>((observer) => {
        if (this.navCheckObserver) {
          this.navCheckObserver.complete();
          this.navCheckObserver.unsubscribe();
        }
        this.navCheckObserver = observer;
      });
    }
    return this.navCheckObservable;
  }

  attemptToNavigate(navigate: Function, reject: Function) {
    let outstandingChecks = this.navCheckObserver ? 1 : 0;
    if (outstandingChecks == 0) {
      navigate();
      return;
    }

    let canNavigate = true;

    let processResult = () => {
      if (canNavigate) {
        navigate();
      } else {
        reject();
      }
    };

    this.navCheckObserver.next({
      accept: () => {
        outstandingChecks--;
        if (outstandingChecks === 0) {
          processResult();
        }
      },
      reject: () => {
        outstandingChecks--;
        canNavigate = false;
        if (outstandingChecks === 0) {
          processResult();
        }
      },
    });
  }

  ngOnDestroy() {
    this.navObserver.complete();
    this.navObserver.unsubscribe();
  }

  goToPatientProfile(chartId) {
    this.updateUrls();
    if (this.mobileUtils.isMobilePlatform()) {
      this.goToInternalPage("VisitsPage", { patChartId: chartId });
    } else {
      let patientChartUrl = `${this.newPatientChartUrl}/patient-charts/${chartId}?externalId=true`;

      const promise = this.http.get(patientChartUrl).toPromise();
      promise
        .then((data) => {
          this.newPatientChartData = data;
          if (this.newPatientChartData && this.newPatientChartData.id)
            this.setSessionStorage(chartId, this.newPatientChartData.id);
          let status: string = this.convertStatusUrlSection(
            this.newPatientChartData.patientStatus
          );
          let resourceUrl = `${this.newPatientProfileBaseUrl}#/referral-tabs/${status}/patient-details/${this.newPatientChartData.id}`;
          this.stateData.setCurrentPage("ReferralTabsPage", { type: status });
          this.goToExternalPage(resourceUrl);
        })
        .catch((error) => {
          console.log(
            "Error: Feature Toggle not available  -> " + JSON.stringify(error)
          );
          window.location.replace(
            `${this.patientProfileUrl}/clinical/patientchart/index/${chartId}`
          );
        });
    }
  }

  updateUrls() {
    this.newPatientProfileBaseUrl =
      this.configurationService.getConfig().clinicalUrl;
    this.patientProfileUrl = this.configurationService.getConfig().logoutUrl;
    this.newPatientChartUrl =
      this.configurationService.resolvePatientChartsUrl();
  }

  public convertStatusUrlSection(patientStatus: string) {
    if (patientStatus.toLowerCase() == "on-service") {
      return "patient";
    }
    if (
      patientStatus.toLowerCase() == "discharged" ||
      patientStatus.toLowerCase() == "cancelled"
    ) {
      return "discharge";
    }
    return patientStatus.toLowerCase();
  }

  sendtoVisitsPage(patId) {
    this.updateUrls();
    let patientChartUrl = `${this.newPatientChartUrl}/patient-charts/${patId}?externalId=true`;

    const promise = this.http.get(patientChartUrl).toPromise();
    promise
      .then((data) => {
        this.newPatientChartData = data;
        if (this.newPatientChartData && this.newPatientChartData.id) {
          this.setSessionStorage(patId, this.newPatientChartData.id);
        }
        this.goToInternalPage(`VisitsPage`, { patChartId: patId });
      })
      .catch((error) => {
        console.log(
          "Error: Patient is not available  -> " + JSON.stringify(error)
        );
      });
  }

  setSessionStorage(patId, patientChartId) {
    window.sessionStorage.setItem("externalId", patId);
    window.sessionStorage.setItem("patientChartId", patientChartId);
    window.sessionStorage.setItem("patChartId", patientChartId);
    let patientChartInfo = {
      id: patId,
      externalId: false,
      patientChartId: patientChartId,
    };
    window.sessionStorage.setItem(
      "patientChartInfo",
      JSON.stringify(patientChartInfo)
    );
    this.events.publish("patientChartIdChange", patientChartInfo);
  }

  // Given an external (QBOS) patient-chart id, fetch the chart to resolve the
  // internal id and atomically write all 4 sessionStorage patient-context keys.
  // Callers should chain navigation off the returned promise so the next page
  // reads fresh context instead of the previous patient's. The resolved chart
  // is also returned so callers that need additional context (e.g. patientStatus
  // for routing) can read it without a second HTTP call.
  resolvePatientSessionFromExternalId(
    externalId
  ): Promise<PatientChartResponse | null> {
    if (!externalId) return Promise.resolve(null);
    this.updateUrls();
    const patientChartUrl = `${this.newPatientChartUrl}/patient-charts/${externalId}?externalId=true`;
    return this.http
      .get<PatientChartResponse>(patientChartUrl)
      .toPromise()
      .then((data) => {
        if (data && data.id) {
          this.setSessionStorage(externalId, data.id);
        }
        return data || null;
      })
      .catch(() => {
        // Navigation should still proceed on lookup failure — matches the
        // fall-through behavior of goToPatientProfile / sendtoVisitsPage.
        return null;
      });
  }

  // Atomically clear the 4 patient-context sessionStorage keys plus the
  // accumulated `navStatus_<assessmentId>` per-visit cache entries written by
  // navigation-data.service. Use at exit points that leave patient context
  // (sidebar Dashboard, 3-dot menu, app launcher) — anywhere a 4-key
  // removeItem block previously lived inline.
  clearPatientSessionContext(): void {
    window.sessionStorage.removeItem("patientChartInfo");
    window.sessionStorage.removeItem("patientChartId");
    window.sessionStorage.removeItem("externalId");
    window.sessionStorage.removeItem("patChartId");
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i);
      if (key && key.indexOf("navStatus_") === 0) {
        window.sessionStorage.removeItem(key);
      }
    }
  }

  // HIS-flow setter: callers in the HIS reports tab only have an external
  // (QBOS) id at hand and historically wrote `externalId: true` in
  // patientChartInfo + isHisRequest=true to force the brief-bar to use the
  // external id. Preserves that semantic while writing all 4 keys atomically.
  setHisPatientSessionContext(externalId): void {
    if (!externalId) return;
    const id = String(externalId);
    window.sessionStorage.setItem("externalId", id);
    window.sessionStorage.setItem("patientChartId", id);
    window.sessionStorage.setItem("patChartId", id);
    window.sessionStorage.setItem("isHisRequest", "true");
    const patientChartInfo = {
      id: externalId,
      externalId: true,
      patientChartId: externalId,
    };
    window.sessionStorage.setItem(
      "patientChartInfo",
      JSON.stringify(patientChartInfo)
    );
    this.events.publish("patientChartIdChange", patientChartInfo);
  }
}



// WEBPACK FOOTER //
// ./src/navigation/navigation.service.ts